// src/oura/components/visibility/VisTiles.tsx
//
// The three answers first: how many questions name the business, the split
// by engine (the only rings in the product), and where it sits on Google.

import { useEffect, useState } from "react";
import { BotIcon, SearchIcon, EyeIcon } from "lucide-react";
import { Delta, EngineLogo, FOCUS_RING, OfValue, PanelIcon, PillButton, Ring, SURFACE, TYPE, Tile, Tiles, type Area } from "@oura/ui";
import { useViewAs } from "@hook/use-view-as";
import { connectMyAccess, getMyOnboarding, type AccessStatus } from "@service/aeo/onboarding";
import { clientArea, MERCHANT_V2 } from "../../merchantV2";
import { safeConnectRedirect } from "../../lib/safeRedirect";
import { cn } from "../../lib/cn";
import { VIS_ENGINES, pct, type EngineFilter, type TrendDelta, type VisEngine, type VisModel, namedRule } from "../../visibility/model";
import { dayMonth, monthName } from "../../visibility/format";
import type { GscState } from "../../gscState";

/**
 * The neutral line for Google's AI pages still being read (ourteam-backend#5619).
 * Not an error, and not a miss: the pages are neither failed nor "not named".
 * Staff on a finished read that left pages unread are told to refresh; the
 * refresh button itself is the page's existing one, beside the title.
 */
function googlePendingText(model: VisModel, staff: boolean): string | null {
    const p = model.googlePending;
    if (!p) return null;
    if (p.pages !== null && p.rereading === false && staff) {
        return p.pages === 1
            ? "1 page was not read. Refresh Google's AI to read it."
            : `${p.pages} pages were not read. Refresh Google's AI to read them.`;
    }
    if (p.pages !== null && p.of !== null) {
        const noun = p.of === 1 ? "page" : "pages";
        const verb = p.pages === 1 ? "is" : "are";
        return `Google's AI: ${p.pages} of ${p.of} ${noun} ${verb} still being read`;
    }
    return p.sentence ?? "Google's AI is still being read";
}

function EngineRing({
    model,
    engine,
    active,
    onToggle,
}: {
    model: VisModel;
    engine: VisEngine;
    active: EngineFilter;
    onToggle: (e: VisEngine) => void;
}) {
    const s = model.engines[engine];
    const value = pct(s.named, s.of);
    const dim = active !== "all" && active !== engine;
    // Google's AI with nothing read yet is being read, not "not read".
    const unread = engine === "ai_overview" && model.googlePending !== null ? "Being read" : "Not read";
    const body = (
        <>
            <Ring value={value} dashed={value === null}>
                <span className={cn(TYPE.tc, "font-semibold tabular-nums", value === null ? "text-gray-400" : "text-gray-900")}>
                    {value === null ? "–" : `${value}%`}
                </span>
            </Ring>
            {/* The name wraps under its logo, and at its spaces only, when a
                third of the tile is too narrow for both on one line: at
                1024px "Google's AI" beside its logo ran into the next ring. */}
            <span className={cn(TYPE.tc, "mt-2 flex max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 font-medium text-gray-900")}>
                <EngineLogo engine={engine} size={14} />
                <span>{s.label}</span>
            </span>
            <span className={cn(TYPE.tm, "tabular-nums text-gray-500")}>
                {s.named !== null && s.of !== null ? `${s.named} of ${s.of}` : unread}
            </span>
        </>
    );
    const shell = cn("flex min-w-0 flex-col items-center rounded-xl py-1 text-center transition-opacity", dim && "opacity-30");
    // An engine with no verdict per question cannot filter the page: every
    // row would read "not measured". It still states its total.
    if (!s.perQuestion) {
        return (
            <div className={shell} title={s.skipped ?? `${s.label} is read as a total on this scan, not per question.`}>
                {body}
            </div>
        );
    }
    return (
        <button
            type="button"
            aria-pressed={active === engine}
            title={active === engine ? "Show all AIs" : `Show ${s.label} only`}
            onClick={() => onToggle(engine)}
            className={cn(shell, "hover:bg-gray-50", FOCUS_RING)}
        >
            {body}
        </button>
    );
}

export function VisTiles({
    model,
    staff,
    engine,
    delta,
    onEngine,
    onToTable,
    googleTo,
    gscState,
}: {
    model: VisModel;
    staff: boolean;
    engine: EngineFilter;
    /** The named-count move since the previous scan, when the history has it. */
    delta: TrendDelta | null;
    onEngine: (e: EngineFilter) => void;
    onToTable: () => void;
    /** Search performance, for both roles. */
    googleTo?: string;
    /**
     * What the Search Console connection actually is (`gscState.ts`), the same
     * state Overview reads. Absent until the connector catalog has arrived, and
     * for a fixture, in which case the card falls back to its own reading.
     */
    gscState?: GscState;
}) {
    const h = model.headline;
    const g = model.google;
    // A grant exists but this scan carries no search data: not measured, and
    // never "Not connected", which Overview and the table banner contradict
    // (#4235). Without a grant, or with its setup unfinished, it is not connected.
    const grantedNoData = !g.connected && !!gscState && gscState.kind !== "not_connected" && gscState.kind !== "action_needed";
    const pendingLine = googlePendingText(model, staff);
    const aiOf = model.engines.ai_overview?.of ?? null;
    const aiShown = aiOf !== null && h.asked > 0 && aiOf < h.asked ? { of: aiOf, asked: h.asked } : null;
    const since = delta?.since ? (staff ? dayMonth(delta.since) : monthName(delta.since)) : "";
    const sub = [
        delta?.comparable ? <Delta key="d" value={delta.value} comparable suffix={since ? `since ${since}` : undefined} /> : null,
        staff
            ? h.verdict ? `${h.verdict} visibility` : null
            : h.benchmark !== null ? `typical: ${h.benchmark} of ${h.of}` : null,
    ].filter(Boolean);

    // The merchant redesign, and only with its flag on: a flag-off merchant
    // keeps the tiles as they were.
    const merchantV2 = !staff && MERCHANT_V2;
    // Staff keep the area colours; the redesign's merchant icons are grey
    // furniture, as on every other client page (clientArea).
    const area = (a: Area) => (merchantV2 ? clientArea(a) : a);

    return (
        <Tiles cols={3}>
            <Tile
                // The count is every engine's; with one engine chosen below,
                // the label says so rather than letting it pass for that one.
                label={
                    <>
                        {staff ? "AI assistants name it" : "AI assistants name you"}
                        {engine !== "all" && <span className="font-normal text-gray-400">All AIs</span>}
                    </>
                }
                icon={{ icon: BotIcon, area: area("ai") }}
                value={h.of > 0 ? <OfValue n={h.named} of={h.of} /> : "–"}
                // How the number is counted, said where it is shown: the
                // tester could not work out how 43 came from 36/37/27, and the
                // rule was nowhere on the page (#4025 FE-5).
                sub={h.of > 0
                    ? (
                        <>
                            {sub.length > 0 && sub.map((part, i) => <span key={i}>{i > 0 && " · "}{part}</span>)}
                            <span className="mt-0.5 block text-gray-400">{namedRule(model.counted, staff)}</span>
                        </>
                    )
                    : h.absent}
                onClick={onToTable}
            />
            <div className={cn(SURFACE.card, "flex min-h-[132px] flex-col p-4")}>
                <span className={cn(TYPE.lab, "flex items-center gap-2")}>
                    <PanelIcon icon={EyeIcon} area={area("ai")} size={16} />
                    By AI
                </span>
                <div className="mt-2 grid grid-cols-3 gap-2">
                    {VIS_ENGINES.map((e) => (
                        <EngineRing
                            key={e}
                            model={model}
                            engine={e}
                            active={engine}
                            onToggle={(k) => onEngine(engine === k ? "all" : k)}
                        />
                    ))}
                </div>
                {pendingLine && <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>{pendingLine}</p>}
                {/* Why Google's AI is "of 39" beside "of 50": its total counts only the
                    questions Google could be read on, a failed read being unknown (#4373).
                    A question with no AI answer from Google is still a read and still in the
                    total (#3945). A client read a lower total as an error (usability test,
                    14 Oct 2026), so the line says what it counts. */}
                {!staff && !pendingLine && aiShown && (
                    <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>Google&rsquo;s AI was read on {aiShown.of} of {aiShown.asked} questions.</p>
                )}
            </div>
            {/* A scan with a grant but no search data stays "Not measured"
                (#4235): connecting again would not change it. */}
            {merchantV2 && !g.connected && !grantedNoData ? (
                <ConnectGoogleTile />
            ) : (
            <Tile
                label="Google search"
                icon={{ icon: SearchIcon, area: area("google") }}
                value={g.connected && g.avgPosition !== null
                    ? <OfValue n={g.avgPosition.toFixed(1)} unit="avg position" />
                    : <span className={TYPE.tt}>{g.connected ? "No searches yet" : grantedNoData ? "Not measured" : "Not connected"}</span>}
                sub={g.connected
                    ? [
                        g.top10 !== null ? `${g.top10} searches in the top 10` : null,
                        g.clicks !== null ? `${g.clicks.toLocaleString("en-GB")} ${staff ? "clicks" : "visits"}${g.days ? `, ${g.days} days` : ""}` : null,
                    ].filter(Boolean).join(" · ")
                    : grantedNoData
                        ? gscState?.label
                    : staff
                        ? "Connect Search Console to measure where the site ranks"
                        // The merchant's name for Angel's role, as on Work and Home (YW, 29 Sep 2026).
                        : "Your account manager links Search Console for you"}
                to={googleTo}
            />
            )}
        </Tiles>
    );
}

const NOT_SAVED = "That did not go through. Try again in a moment.";
const NO_GOOGLE_SCREEN = "We could not open Google's connection screen. Try again in a moment.";

type ConnectionState =
    | { kind: "loading" }
    | { kind: "ready"; status: AccessStatus | null }
    | { kind: "error" };

/**
 * The merchant's Google search tile while this scan has no Search Console
 * data: an empty state with the one thing that changes it, connecting
 * Search Console, done from here. It is the same connection Setup holds, so
 * Setup shows it connected afterwards, and once it is connected this tile
 * says the figures are on their way instead of offering it again.
 */
function ConnectGoogleTile() {
    // Loading and a failed read are not "Not connected": absent is unknown.
    // The button shows only once the set-up says the access is not held, so
    // a merchant who is connected is never sent through Google again.
    const [connection, setConnection] = useState<ConnectionState>({ kind: "loading" });
    const [busy, setBusy] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    const { active: readOnly } = useViewAs();

    useEffect(() => {
        let live = true;
        getMyOnboarding()
            .then((m) => {
                if (live) setConnection({ kind: "ready", status: m?.access.find((a) => a.key === "gsc")?.status ?? null });
            })
            .catch(() => {
                if (live) setConnection({ kind: "error" });
            });
        return () => {
            live = false;
        };
    }, []);

    const connect = async () => {
        setBusy(true);
        setFailure(null);
        try {
            const result = await connectMyAccess("gsc", {});
            if (!result.ok) {
                setFailure(result.message);
                setBusy(false);
                return;
            }
            if (result.kind === "redirect") {
                // Google's own screen takes over; the button stays busy until
                // the page leaves. Only an address we expect is followed.
                const to = safeConnectRedirect(result.url);
                if (!to) {
                    setFailure(NO_GOOGLE_SCREEN);
                    setBusy(false);
                    return;
                }
                window.location.assign(to);
                return;
            }
            setConnection({ kind: "ready", status: result.onboarding.access.find((a) => a.key === "gsc")?.status ?? "connected" });
        } catch {
            setFailure(NOT_SAVED);
        }
        setBusy(false);
    };

    // No set-up record, or no Search Console line on it, is unknown too: the
    // button is offered only for a line that says it has not been started.
    const status = connection.kind === "ready" ? connection.status : null;
    const held = status === "connected" || status === "confirmed";
    const mayConnect = status === "not_started";
    const title = connection.kind === "loading"
        ? "Checking…"
        : connection.kind === "error" || status === null
            ? "Not known"
            : held
                ? "Connected"
                : status === "claimed"
                    ? "Waiting for us"
                    : "Not connected";
    const line = connection.kind === "loading"
        ? "Checking your Google connection."
        : connection.kind === "error"
            ? "We could not check your Google connection. Reload to try again."
            : status === null
                ? "Your account manager sets up the Google connection with you."
                : held
                    ? "Your Google figures appear here after your next check."
                    : status === "claimed"
                        ? "We are checking the Google access you gave us."
                        : "Connect Search Console to see how people find you on Google.";
    return (
        <div className={cn(SURFACE.card, "flex min-h-[132px] flex-col p-4")}>
            <span className={cn(TYPE.lab, "flex items-center gap-2")}>
                <PanelIcon icon={SearchIcon} area={clientArea("google")} size={16} />
                Google search
            </span>
            <p className={cn(TYPE.tt, "m-0 mt-1 text-gray-900")}>{title}</p>
            <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>{line}</p>
            {mayConnect && (
                <div className="mt-auto pt-3">
                    <PillButton variant="primary" size="sm" busy={busy} disabled={readOnly} onClick={() => void connect()}>
                        Connect Google
                    </PillButton>
                </div>
            )}
            {failure && <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 font-medium text-rose-600")}>{failure}</p>}
        </div>
    );
}
